import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
	activateCampaign,
	advanceCampaign,
	type Campaign,
	type CampaignResult,
	type CampaignStage,
	type CreateCampaignInput,
	completeCampaign,
	createCampaign,
	listCampaignResults,
	listCampaigns,
	pauseCampaign,
	retryDevice,
	rollbackDevice,
} from "./update-campaigns-api";

export const CAMPAIGNS_QUERY_KEY = ["device-agent", "update-campaigns"] as const;
export const CAMPAIGN_RESULTS_QUERY_KEY = (campaignId: number) =>
	["device-agent", "update-campaign-results", campaignId] as const;

export function useCampaignsQuery() {
	return useQuery<Campaign[]>({
		queryKey: CAMPAIGNS_QUERY_KEY,
		queryFn: listCampaigns,
	});
}

/**
 * Per-kampania wyniki urządzeń. `enabled` gatuje pobranie do momentu rozwinięcia
 * podglądu. refetchInterval utrzymuje świeży obraz rolloutu (statusy zmieniają
 * się asynchronicznie gdy agent raportuje update_progress).
 */
export function useCampaignResultsQuery(campaignId: number | null, enabled: boolean) {
	return useQuery<CampaignResult[]>({
		queryKey: CAMPAIGN_RESULTS_QUERY_KEY(campaignId ?? 0),
		queryFn: () => listCampaignResults(campaignId as number),
		enabled: enabled && campaignId !== null,
		refetchInterval: enabled ? 5000 : false,
	});
}

export function useCreateCampaign() {
	const queryClient = useQueryClient();
	return useMutation<Campaign, Error, CreateCampaignInput>({
		mutationFn: createCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export function useActivateCampaign() {
	const queryClient = useQueryClient();
	return useMutation<Campaign, Error, number>({
		mutationFn: activateCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export interface AdvanceCampaignInput {
	campaignId: number;
	stage: CampaignStage;
}

export function useAdvanceCampaign() {
	const queryClient = useQueryClient();
	return useMutation<Campaign, Error, AdvanceCampaignInput>({
		mutationFn: ({ campaignId, stage }) => advanceCampaign(campaignId, stage),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export function usePauseCampaign() {
	const queryClient = useQueryClient();
	return useMutation<Campaign, Error, number>({
		mutationFn: pauseCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export function useCompleteCampaign() {
	const queryClient = useQueryClient();
	return useMutation<Campaign, Error, number>({
		mutationFn: completeCampaign,
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export interface RollbackDeviceInput {
	deviceUuid: string;
	campaignId: number;
}

export function useRollbackDevice() {
	const queryClient = useQueryClient();
	return useMutation<CampaignResult, Error, RollbackDeviceInput>({
		mutationFn: ({ deviceUuid, campaignId }) => rollbackDevice(deviceUuid, campaignId),
		onSuccess: (result) => {
			queryClient.invalidateQueries({
				queryKey: CAMPAIGN_RESULTS_QUERY_KEY(result.campaignId),
			});
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}

export function useRetryDevice() {
	const queryClient = useQueryClient();
	return useMutation<CampaignResult, Error, RollbackDeviceInput>({
		mutationFn: ({ deviceUuid, campaignId }) => retryDevice(deviceUuid, campaignId),
		onSuccess: (result) => {
			queryClient.invalidateQueries({
				queryKey: CAMPAIGN_RESULTS_QUERY_KEY(result.campaignId),
			});
			queryClient.invalidateQueries({ queryKey: CAMPAIGNS_QUERY_KEY });
		},
	});
}
